import { useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import { _clientSettings } from '../../config/client-settings';
import {
    AppBar,
    CssBaseline,
    Divider,
    Drawer,
    Hidden,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Typography,
    LinearProgress,
    Dialog
} from '@material-ui/core';
import {
    AuthContext,
    NavContext,
    NavRoutes,
    UserContext
} from '../contexts';
import { CrumbsBar } from './CrumbsBar';
import { ApplicationRoles } from '../../helpers';
import MenuIcon from '@material-ui/icons/Menu';
import LockIcon from '@material-ui/icons/LockOutlined';
import ExitToAppIcon from '@material-ui/icons/ExitToAppOutlined';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { AuthenticationResult } from '@azure/msal-browser';
import { aquireToken } from '../contexts/AuthContext';

const drawerWidth = 240;
const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
    },
    drawer: {
        /*[theme.breakpoints.up('lg')]: {
            width: drawerWidth,
            flexShrink: 0
        },*/
    },
    appBar: {
        backgroundColor: '#f5f2f0',
        /*[theme.breakpoints.up('lg')]: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: drawerWidth
        },*/
    },
    menuButton: {
        marginRight: theme.spacing(2),
        //[theme.breakpoints.up('lg')]: {
        //display: 'none'
        //}
    },
    toolbar: theme.mixins.toolbar,
    toolbarButton: {
        margin: theme.spacing(1)
    },
    drawerPaper: {
        width: drawerWidth,
        backgroundColor: '#f5f2f0'
    },
    content: {
        width: '100%',
        flexGrow: 1,
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(3),
        },
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(1),
        },
    },
    title: {
        flexGrow: 1
    },
    user: {
        margin: theme.spacing(1),
        flexGrow: 1,
        textAlign: 'right'
    },
    reportContainer: {
        "&:hover": {
            backgroundColor: "rgb(233,230,228)"
        },
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        padding: '11px 0px',
        transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms'
    },
    reportBody: {
        display: 'flex',
        flexDirection: 'row'
    },
    reportName: {
        alignSelf: 'center',
        marginLeft: '10px',
        fontSize: '1rem'
    }
}));

export const Layout = ({ ...props }) => {
    const { container } = props;
    const classes = useStyles();
    const theme = useTheme();
    const { instance } = useMsal();
    const isAuthenticated: boolean = useIsAuthenticated();
    const { logOut } = useContext(AuthContext);
    const { menuItems, navigateTo, clearBreadcrumbs } = useContext(NavContext);

    const {
        userInfo,
        getUserInfoAsync,
        getGlobalSettingsAsync,
        globalSettings,
        isUserInRoles,
        getBudgetYearsAsync,
        budgetYears,
        getBudgetSessionAsync,
        getAllReportsAsync,
        allReports,
        updatedReport,
        shouldMenuClose,
        selectedYear,
        setSelectedYear
    } = useContext(UserContext);

    const [mobileOpen, setMobileOpen] = useState(false);
    const [username, setUsername] = useState(null);
    const [ready, setIsReady] = useState(false);
    const [isDialog, setIsDialog] = useState(false);
    
    const originalPrivacyPolicy: HTMLElement = document.getElementById("otnotice-26ab4484-ed83-439f-b8f9-948d2e7ccbc8");

    const privacyPolicyDialog = (): JSX.Element => {
        const clonedPrivacyPolicy = originalPrivacyPolicy.cloneNode(true) as HTMLElement;
        clonedPrivacyPolicy.style.display = "block";

        return (
            <Dialog
                open={isDialog}
                onClose={() => setIsDialog(false)}
                aria-labelledby="form-dialog-title"
            >
                <div style={{padding: "30px"}}>
                    <div dangerouslySetInnerHTML={{ __html: clonedPrivacyPolicy.outerHTML }}></div>
                </div>
            </Dialog>
        );
    };

    useEffect(() => {
        if (shouldMenuClose)
            setMobileOpen(false);
    }, [shouldMenuClose])

    const drawerToggleHandler = () => {
        setMobileOpen(!mobileOpen);
    };

    const location = useLocation();

    const itemClickHandler = (item): void => {
        if (item.routePath !== location.pathname) {
            clearBreadcrumbs();
            navigateTo(item.routePath);
        }
    };

    const logoutClickHandler = (): void => {
        logOut();
    };

    const goToAdminClickHandler = (): void => {
        navigateTo(NavRoutes.Admin.Dashboard);
    };
    
    const parseJwt = async (): Promise<void> => {
        const token: AuthenticationResult = await aquireToken(instance);
        const base64Url: string = token.accessToken.split('.')[1];
        const base64: string = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload: string = decodeURIComponent(
            atob(base64)
            .split('')
            .map((char: string): string =>
                '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2)
            )
            .join('')
        );

        const parsedToken = JSON.parse(jsonPayload);
        setUsername(`${parsedToken.family_name} ${parsedToken.given_name}`);
        getGlobalSettingsAsync();
        getBudgetYearsAsync();
        getAllReportsAsync();
        getUserInfoAsync();
    };

    useEffect((): void => {
        if (isAuthenticated)
            parseJwt();
    }, [isAuthenticated]);

    useEffect((): void => {
        if (
            userInfo?.externalKey !== null &&
            username &&
            budgetYears &&
            globalSettings &&
            allReports
        ){
            setIsReady(true);

            if (userInfo.roles === null || userInfo.roles.length === 0)
                navigateTo(NavRoutes.Forbidden);
        }
    }, [userInfo, username, budgetYears, globalSettings, allReports]);

    useEffect((): void => {
        if (updatedReport !== null)
            getAllReportsAsync();
    }, [updatedReport]);

    useEffect((): void => {
        if (budgetYears !== null) {
            let activeYears = [];

            if (budgetYears.items && budgetYears.items.length > 0)
                budgetYears.items.map(session => {
                    if (session.isActive)
                        activeYears.push(session.budgetYearId);
                });

            //first time
            if (!selectedYear) {
                //default year is the first active one...
                if (activeYears.length > 0)
                    setSelectedYear(activeYears[0]);
                //...or is the first year in general
                else if (budgetYears.items.length > 0)
                    setSelectedYear(budgetYears.items[0].budgetYearId);
            }
        }
    }, [budgetYears]);

    const drawer = (
        <div>
            <div className={classes.toolbar} />
            <Divider />

            {budgetYears &&
                <div style={{ textAlign: 'center' }}>
                    <label style={{ fontSize: "1rem" }}>
                        Selected year:
                    </label>
                    <Select
                        style={{ width: "80px", marginLeft: "10px" }}
                        value={selectedYear ? selectedYear : ""}
                        onChange={x => {
                            //value in select
                            setSelectedYear(x.target.value);
                            //value to send to UserContext for AllProjects and MyProjects
                            getBudgetSessionAsync(x.target.value, "dropdown");
                            
                        }}
                        //displayEmpty
                        disableUnderline={false}
                    >
                        {budgetYears &&
                            budgetYears.items &&
                            budgetYears.items.length > 0 &&
                            budgetYears.items.map((year, index) => {
                                return (
                                    <MenuItem key={index} value={year.budgetYearId}>
                                        {year.observedYear}
                                    </MenuItem>
                                );
                            })}
                    </Select>
                </div>}

            <List>
                {menuItems && menuItems
                    .map((item, index) => {
                        if (item.title == "Home")
                            return (
                                <ListItem
                                    key={index}
                                    button
                                    onClick={() => itemClickHandler(item)}>
                                    <ListItemIcon>{item.icon !== undefined ? item.icon : <MenuIcon />}</ListItemIcon>
                                    <ListItemText primary={item.title || item.routePath} />
                                </ListItem>
                            );
                    }
                    )}

                {menuItems && menuItems
                    .filter(item => (!item.isAdmin || item.isAdmin === false)
                        && (item.roles === null || item.roles === undefined || isUserInRoles(item.roles)))
                    .map((item, index) => {
                        if (item.title == "All Projects" ||
                            (
                                item.title == "My Projects" &&
                                isUserInRoles([
                                    ApplicationRoles.Owner,
                                    ApplicationRoles.ProjectManager
                                ])
                            )
                        )
                            return (
                                <ListItem
                                    key={index}
                                    button
                                    onClick={() => itemClickHandler(item)}>
                                    <ListItemIcon>{item.icon !== undefined ? item.icon : <MenuIcon />}</ListItemIcon>
                                    <ListItemText primary={item.title || item.routePath} />
                                </ListItem>
                            );
                    }
                    )}

                {menuItems && menuItems
                    .filter(item => item.roles && item.roles.length > 0 && isUserInRoles(item.roles))
                    .map((item, index) => {
                        if (item.title == "Manage project managers")
                            return (
                                <ListItem
                                    key={index}
                                    button
                                    onClick={() => itemClickHandler(item)}>
                                    <ListItemIcon>{item.icon !== undefined ? item.icon : <MenuIcon />}</ListItemIcon>
                                    <ListItemText primary={item.title || item.routePath} />
                                </ListItem>
                            );
                    })
                }

            </List>

            {
                allReports &&
                allReports.items &&
                allReports.items.map((report, index) => {
                    return (
                        <div key={index} className={classes.reportContainer} onClick={() => window.open(report.reportUrl, "_blank")}>
                            <div className={classes.reportBody}>
                                <svg style={{ width: '1em', height: '1em', minWidth: '56px' }} className="MuiSvgIcon-root MuiSvgIcon-colorPrimary" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
                                    <path d="M20.38 8.57l-1.23 1.85a8 8 0 01-.22 7.58H5.07A8 8 0 0115.58 6.85l1.85-1.23A10 10 0 003.35 19a2 2 0 001.72 1h13.85a2 2 0 001.74-1 10 10 0 00-.27-10.44z" />
                                    <path d="M10.59 15.41a2 2 0 002.83 0l5.66-8.49-8.49 5.66a2 2 0 000 2.83z" />
                                </svg>
                                <div className={classes.reportName}>
                                    {report.name}
                                </div>
                            </div>
                        </div>
                    );
                })
            }

            <div className={classes.reportContainer} onClick={() => window.open(_clientSettings.StaticReport, "_blank")}>
                <div className={classes.reportBody}>
                    <svg style={{ width: '1em', height: '1em', minWidth: '56px' }} className="MuiSvgIcon-root MuiSvgIcon-colorPrimary" focusable="false" viewBox="0 0 24 24" aria-hidden="true" role="presentation">
                        <path d="M20.38 8.57l-1.23 1.85a8 8 0 01-.22 7.58H5.07A8 8 0 0115.58 6.85l1.85-1.23A10 10 0 003.35 19a2 2 0 001.72 1h13.85a2 2 0 001.74-1 10 10 0 00-.27-10.44z" />
                        <path d="M10.59 15.41a2 2 0 002.83 0l5.66-8.49-8.49 5.66a2 2 0 000 2.83z" />
                    </svg>
                    <div className={classes.reportName}>
                        All Power BI reports
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={classes.root}>
            {ready ?
                <>
                    <CssBaseline />
                    <AppBar position="fixed" className={classes.appBar}>
                        <Toolbar>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={drawerToggleHandler}
                                className={classes.menuButton}>
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" noWrap className={classes.title}>
                                <CrumbsBar />
                            </Typography>
                            {/* <div style={{display: 'flex', flexGrow: 1, justifyContent: 'flex-end'}}>
                                <div style={{marginRight: '20px'}}>
                                    Current budget session:
                                </div>
                                <div style={{display: 'flex'}}>
                                    {changedActiveYear &&
                                    changedActiveYear.currentBudgetSession &&
                                    <div style={{whiteSpace: 'nowrap', alignSelf: 'center', marginRight: '20px'}}>
                                        {changedActiveYear.observedYear}&nbsp;&nbsp;&nbsp;{changedActiveYear.currentBudgetSession.name}
                                    </div>}
                                </div>
                            </div> */}
                            <Hidden smDown>
                                {
                                    originalPrivacyPolicy &&
                                    privacyPolicyDialog()
                                }
                                
                                <Typography variant="body1" noWrap className={classes.user}>
                                    {
                                        originalPrivacyPolicy &&
                                        <Typography
                                            color="textSecondary"
                                            component="strong"
                                            onClick={() => setIsDialog(true)}
                                            style={{
                                                cursor: 'pointer',
                                                marginRight: '50px'
                                            }}
                                        >
                                            Privacy Policy
                                        </Typography>
                                    }

                                    welcome <Typography color="textSecondary" component="strong">{username} ({
                                        userInfo &&
                                        userInfo.roles &&
                                        userInfo.roles.map((role, roleIndex) => {
                                            return (
                                                <span key={roleIndex}>
                                                    {
                                                        roleIndex === 0 ?
                                                            role.roleName :
                                                            ", " + role.roleName
                                                    }
                                                </span>
                                            );
                                        })})</Typography>
                                </Typography>
                            </Hidden>
                            {isUserInRoles([ApplicationRoles.Administrator, ApplicationRoles.DataQuality]) &&
                                <IconButton aria-label="admin area" className={classes.toolbarButton} color="inherit" onClick={goToAdminClickHandler}>
                                    <LockIcon color="primary" />
                                </IconButton>
                            }
                            <IconButton aria-label="logout" className={classes.toolbarButton} color="inherit" onClick={logoutClickHandler}>
                                <ExitToAppIcon color="primary" />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    <nav className={classes.drawer} aria-label="menu items">
                        {/* Mobile Screens */}
                        {/* <Hidden lgUp implementation="js"> */}
                        <Drawer
                            container={container}
                            variant="temporary"
                            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                            open={mobileOpen}
                            onClose={drawerToggleHandler}
                            classes={{
                                paper: classes.drawerPaper,
                            }}
                            ModalProps={{
                                keepMounted: true, // Better open performance on mobile.
                            }}>
                            {drawer}
                        </Drawer>
                        {/* </Hidden> */}
                        {/* Desktop Screens */}
                        {/* <Hidden mdDown implementation="js">
                            <Drawer
                                classes={{
                                    paper: classes.drawerPaper,
                                }}
                                variant="permanent"
                                open>
                                {drawer}
                            </Drawer>
                        </Hidden> */}
                    </nav>
                    <main className={classes.content}>
                        <div className={classes.toolbar} />
                        {props.children}
                    </main>
                </>
                :
                <>
                    <CssBaseline />
                    <main className={classes.content}>
                        <LinearProgress />
                    </main>
                </>
            }
        </div>
    );
}